import { PetAttribute, PetStatusEffect } from '.';
import { createMessage, SB } from '@play-co/replicant';
import createMessages from '../config/messages/createMessages';
import { getAttributeValue } from './pet.getters';
import { petAttributes, petStatusEffects } from './pet.ruleset';
import * as Fate from '../fate';
import { applyFriendVisitDebuff, setAttribute } from './pet.modifiers';

export const petMessages = createMessages({
  /**
   * Change friend pet fate by applying status effects and attributes
   */
  updateFate: createMessage(
    SB.object({
      effects: SB.object(
        petStatusEffects.reduce(
          (acc, cur) => {
            acc[cur] = SB.boolean().optional();
            return acc;
          },
          {} as Record<
            PetStatusEffect,
            SB.BooleanSchema & SB.Optional<boolean>
          >,
        ),
      ),
      attributesMode: SB.tuple(['set', 'add']).optional(),
      attributes: SB.object(
        petAttributes.reduce(
          (acc, cur) => {
            acc[cur] = SB.number().optional();
            return acc;
          },
          {} as Record<PetAttribute, SB.NumberSchema & SB.Optional<number>>,
        ),
      ),
      now: SB.int(),
    }),
    (state, args) => {
      Object.keys(args.effects).forEach((key) => {
        const statusEffect = key as PetStatusEffect;
        const hasStatus = args.effects[statusEffect];
        if (hasStatus === undefined) return;

        Fate.modifiers.resolveStatusEffect(
          state,
          statusEffect,
          hasStatus,
          args.now,
        );
      });

      Object.keys(args.attributes).forEach((key) => {
        const attribute = key as PetAttribute;
        const value = args.attributes[attribute];
        if (value === undefined) return;

        setAttribute(
          state,
          attribute,
          args.attributesMode === 'set'
            ? value
            : getAttributeValue(state, attribute, args.now) + value,
          args.now,
        );
      });

      Fate.modifiers.update(state, args.now);
    },
  ),

  updateStatusEffects: createMessage(
    SB.object(
      petStatusEffects.reduce(
        (acc, cur) => ({ ...acc, [cur]: SB.boolean().optional() }),
        {} as Record<PetStatusEffect, boolean>,
      ),
    ),
    (state, args, { timestamp }) => {
      Object.keys(args).forEach((key) => {
        const statusEffect = key as PetStatusEffect;
        const { resolveStatusEffect } = Fate.modifiers;

        resolveStatusEffect(state, statusEffect, args[statusEffect], timestamp);
      });
      Fate.modifiers.update(state, timestamp);
    },
  ),

  updateAttributes: createMessage(
    SB.object(
      petAttributes.reduce(
        (acc, cur) => ({ ...acc, [cur]: SB.number().optional() }),
        {} as Record<PetAttribute, number>,
      ),
    ),
    (state, args, { timestamp }) => {
      Object.keys(args).forEach((key) => {
        const attribute = key as PetAttribute;
        setAttribute(
          state,
          attribute,
          getAttributeValue(state, attribute, timestamp) + args[attribute],
          timestamp,
        );
      });
      Fate.modifiers.update(state, timestamp);
    },
  ),

  applyVisitDebuff: createMessage(
    SB.object({ love: SB.number(), stomach: SB.number() }),
    (state, args, { timestamp }) => {
      // Set debuff on attribute decay if they came from friend care actions
      if (args.love) {
        applyFriendVisitDebuff(state, 'love', args.love, timestamp);
      }

      if (args.stomach) {
        applyFriendVisitDebuff(state, 'stomach', args.stomach, timestamp);
      }
    },
  ),
});
